import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Shield, Globe, ArrowRight, CheckCircle, Sparkles, 
  X, Package, MapPin, Loader2, Truck, Send, 
  Clock, ChevronDown, MessageCircle
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import SEO from '../components/SEO';


const AboutUs = () => {
  const { isDarkMode } = useTheme();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const bulkForm = useRef();

  // WhatsApp Config
  const WHATSAPP_NUMBER = "91XXXXXXXXXX"; // Apna number yahan dalo (with 91)
  const openWhatsApp = () => {
    const msg = encodeURIComponent("Hey WeThrift! I'm interested in Bulk/Wholesale inventory. Can we discuss pricing?");
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`, '_blank');
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

 const handleBulkSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);

    // Data structure ko naye Backend Model (BulkRequest) ke hisab se set kiya
    const formData = {
      business_name: bulkForm.current.user_name.value, 
      email: bulkForm.current.user_email.value,        
      quantity: bulkForm.current.quantity.value,
      category: bulkForm.current.category.value,
      message: bulkForm.current.message.value,
    };

    try {
      // API endpoint change kiya taaki data Bulk tab mein jaye
      const response = await fetch('https://wethrift-backend.onrender.com/api/bulk/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();

      if (response.ok && data.success) {
        setIsSuccess(true);
        bulkForm.current.reset();
        
        // Modal ko band karne ka logic
        setTimeout(() => {
          setIsSuccess(false);
          setIsModalOpen(false);
        }, 3500);
      } else {
        alert(data.message || "Protocol Error: Backend check karo.");
      }
    } catch (error) {
      console.error("Submission Error:", error);
      alert("Server Offline: Port 5000 check karo bhai.");
    } finally {
      setIsSending(false);
    }
  };
  return (

    <>
    <SEO 
  title="The Mission | Sustainable Fashion" 
  description="Learn about THE_ARCHIVE. We are redefining thrift culture in India through high-end curation and sustainable practices."
  url="/about-us"
/>

    <div className={`min-h-screen transition-colors duration-500 selection:bg-emerald-500/30 ${
      isDarkMode ? 'bg-black text-white' : 'bg-white text-zinc-900'
    }`}>
      
      {/* --- HERO SECTION --- */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-[10px] font-bold tracking-widest uppercase mb-8 ${isDarkMode ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/5' : 'border-emerald-200 text-emerald-600 bg-emerald-50'}`}>
            <Sparkles size={12} /> Curating Since 2026
          </motion.div>
          
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-5xl md:text-8xl font-black tracking-tighter mb-6 uppercase italic">
            WETHRIFT <span className={isDarkMode ? 'text-zinc-800' : 'text-zinc-200'}>ARCHIVE</span>
          </motion.h1>
          
          <p className="max-w-2xl mx-auto text-zinc-500 font-medium text-sm md:text-base leading-relaxed mb-10">
            India's premier archival marketplace. Authenticated vintage pieces delivered nationwide.
          </p>

          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products" className="bg-emerald-500 hover:bg-emerald-400 text-black px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all hover:scale-105 flex items-center gap-2">
              Shop Collection <ArrowRight size={16} />
            </Link>
            <button onClick={() => setIsModalOpen(true)} className={`px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all border ${isDarkMode ? 'border-zinc-800 hover:bg-zinc-900' : 'border-zinc-200 hover:bg-zinc-50'}`}>
              Bulk Inquiry
            </button>
          </div>
        </div>
      </section>

      {/* --- SERVICES/STATS --- */}
      <section className="py-12 px-6">
        <div className="max-w-7xl mx-auto">
           <h2 className="text-center text-xs font-black tracking-[0.4em] uppercase mb-12 opacity-50 italic underline decoration-emerald-500 underline-offset-8">Our Standards</h2>
           <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'States Served', val: '28+', icon: <MapPin size={20}/> },
              { label: 'Authentic', val: '100%', icon: <Shield size={20}/> },
              { label: 'Express', val: 'Shipping', icon: <Truck size={20}/> },
              { label: 'Trusted', val: 'Archive', icon: <Globe size={20}/> },
            ].map((s, i) => (
              <motion.div key={i} whileHover={{ y: -5 }} className={`p-8 rounded-[2rem] border ${isDarkMode ? 'bg-zinc-900/30 border-zinc-800' : 'bg-zinc-50 border-zinc-100'} text-center`}>
                <div className="text-emerald-500 mb-4 flex justify-center">{s.icon}</div>
                <div className="text-2xl md:text-3xl font-black italic mb-1 uppercase">{s.val}</div>
                <div className="text-[10px] uppercase font-bold tracking-widest text-zinc-500">{s.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- BULK SECTION --- */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div {...fadeInUp} className={`rounded-[3rem] overflow-hidden border ${isDarkMode ? 'bg-zinc-900/20 border-zinc-800' : 'bg-zinc-50 border-zinc-200'} p-8 md:p-16`}>
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-4xl md:text-6xl font-black italic uppercase tracking-tighter mb-6 leading-none">
                  Bulk_<span className="text-emerald-500">Inventory</span>
                </h2>
                <p className="text-zinc-500 font-medium mb-8 leading-relaxed">
                  Start or scale your thrift business with our premium wholesale lots. High resale value, curated specifically for the Indian market.
                </p>
                <div className="flex flex-wrap gap-4 mb-10">
                   <button onClick={() => setIsModalOpen(true)} className="bg-white text-black px-8 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-emerald-500 transition-colors">
                    Bulk Inquiry
                  </button>
                  <button onClick={openWhatsApp} className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 px-8 py-4 rounded-xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-500 hover:text-black transition-all">
                    <MessageCircle size={16} /> WhatsApp
                  </button>
                </div>
                <ul className="space-y-3 text-[10px] font-black uppercase tracking-widest opacity-60">
                  <li className="flex items-center gap-2 text-emerald-500"><CheckCircle size={14}/> Min: 100 Pieces</li>
                  <li className="flex items-center gap-2"><CheckCircle size={14}/> 100% Quality Guaranteed</li>
                </ul>
              </div>
              <div className="relative hidden md:flex justify-center">
                <div className="absolute inset-0 bg-emerald-500/5 blur-[80px] rounded-full" />
                <Package size={220} strokeWidth={0.5} className="relative text-emerald-500/20 rotate-12" />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* --- CONTACT GRID --- */}
    <section className="py-20 px-6 max-w-7xl mx-auto">
  <div className="text-center mb-16">
    <h2 className="text-4xl font-black italic uppercase tracking-tighter mb-4">Get_In_Touch</h2>
    <div className="w-12 h-1 bg-emerald-500 mx-auto rounded-full"></div>
  </div>
  
  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
    {[
      { title: "Protocol Email", val: "contact@wethrift.in", icon: <Send size={20}/> },
      { title: "Response Time", val: "Under 24 Hours", icon: <Clock size={20}/> },
      { title: "Bulk Units", val: "100+ Piece Lots", icon: <Package size={20}/> }
    ].map((item, idx) => (
      <div key={idx} className={`p-10 rounded-[2.5rem] border transition-all hover:border-emerald-500/50 ${isDarkMode ? 'bg-zinc-900/40 border-zinc-800' : 'bg-zinc-50 border-zinc-200'}`}>
        <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 text-emerald-500 flex items-center justify-center mb-6">
          {item.icon}
        </div>
        <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-500 mb-2">{item.title}</h4>
        <p className="text-lg font-bold italic">{item.val}</p>
      </div>
    ))}
  </div>

  {/* Buttons Container - Ab ye cards ke niche perfect center hai */}
  <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
    <button 
      onClick={() => setIsModalOpen(true)} 
      className={`px-10 py-5 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all hover:scale-105 active:scale-95 ${
        isDarkMode ? 'bg-white text-black hover:bg-emerald-500' : 'bg-black text-white hover:bg-emerald-600'
      }`}
    >
      Bulk Inquiry
    </button>
    
    <button 
      onClick={openWhatsApp} 
      className="bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 px-10 py-5 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center gap-2 hover:bg-emerald-500 hover:text-black transition-all hover:scale-105 active:scale-95"
    >
      <MessageCircle size={16} /> WhatsApp Support
    </button>
  </div>
</section>

      {/* --- MODAL FORM --- */}
      <AnimatePresence>
        {isModalOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm">
            <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }} className={`relative w-full max-w-xl rounded-[3rem] p-8 md:p-12 border ${isDarkMode ? 'bg-zinc-950 border-zinc-800' : 'bg-white border-zinc-200'}`}>
              
              <button onClick={() => setIsModalOpen(false)} className="absolute top-8 right-8 text-zinc-500 hover:text-emerald-500 transition-colors">
                <X size={24} />
              </button>

              {isSuccess ? (
                <div className="py-12 text-center">
                  <div className="w-20 h-20 bg-emerald-500 text-black rounded-full flex items-center justify-center mx-auto mb-6 shadow-[0_0_30px_rgba(16,185,129,0.3)]">
                    <CheckCircle size={40} />
                  </div>
                  <h3 className="text-3xl font-black uppercase italic mb-2 tracking-tighter">Query_Received</h3>
                  <p className="text-zinc-500 text-xs font-bold uppercase tracking-widest">Wait for our protocol email.</p>
                </div>
              ) : (
                <>
                  <div className="mb-10">
                    <h3 className="text-4xl font-black uppercase italic mb-1 tracking-tighter">Bulk_<span className="text-emerald-500">Inquiry</span></h3>
                    <p className="text-[10px] font-black text-zinc-500 uppercase tracking-[0.3em]">Confidential Wholesale Protocol</p>
                  </div>

                  <form ref={bulkForm} onSubmit={handleBulkSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Identity/Brand</label>
                        <input name="user_name" required placeholder="NAME / BUSINESS" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Digital Mail</label>
                        <input name="user_email" type="email" required placeholder="EMAIL ADDRESS" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Volume (Min 100)</label>
                        <input name="quantity" type="number" min="100" required placeholder="QTY" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Archive Class</label>
                        <div className="relative">
                          <select name="category" className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border appearance-none transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`}>
                            <option>Mixed Vintage</option>
                            <option>Streetwear / Tees</option>
                            <option>Denim / Archive</option>
                            <option>Winter / Jackets</option>
                          </select>
                          <ChevronDown size={14} className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none opacity-50" />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-zinc-500 ml-1">Specifications</label>
                      <textarea name="message" rows="3" placeholder="YOUR REQUIREMENTS..." className={`w-full px-6 py-4 rounded-2xl text-[10px] font-black uppercase outline-none border resize-none transition-all ${isDarkMode ? 'bg-zinc-900 border-zinc-800 focus:border-emerald-500' : 'bg-zinc-100 border-zinc-200 focus:border-emerald-500'}`} />
                    </div>

                    <button disabled={isSending} type="submit" className="w-full bg-emerald-500 hover:bg-emerald-400 text-black py-5 rounded-2xl font-black text-xs uppercase tracking-[0.3em] flex items-center justify-center gap-3 transition-all active:scale-95 disabled:opacity-50">
                      {isSending ? <Loader2 className="animate-spin" size={18}/> : <><Send size={18}/> Dispatch Inquiry</>}
                    </button>
                  </form>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default AboutUs;